/**
 * GeoSim catalog for anaafernandez (production super-admin toolbar).
 * AR province/city = exactamente /admin/wishlist-config (CTA por región).
 */

import type { GeoSimVpnMode } from "./types";
import { findArCityByGeoCode, simCityParamForEntry } from "./arLocations";

export const GEO_SIM_BOOT_PARAM = "geo_sim_boot";
export const GEO_SIM_ELIGIBLE_KEY = "admin_geo_sim_eligible";
export const GEO_SIM_MINT_PATH = "/api/admin/geo-sim-token";

/** Ruta admin canónica (navLabel: "CTA por región"). */
export const CTA_POR_REGION_ADMIN_PATH = "/admin/wishlist-config";

/** Quick ISO-2 chips (intl DualCTA + AR Base). */
export const GEO_SIM_QUICK_COUNTRIES: { code: string; label: string }[] = [
  { code: "AR", label: "AR" },
  { code: "US", label: "US" },
  { code: "MX", label: "MX" },
  { code: "CL", label: "CL" },
  { code: "CO", label: "CO" },
  { code: "BR", label: "BR" },
  { code: "ES", label: "ES" },
  { code: "DE", label: "DE" },
  { code: "FR", label: "FR" },
  { code: "GB", label: "GB" },
  { code: "IT", label: "IT" },
  { code: "PE", label: "PE" },
  { code: "UY", label: "UY" },
];

export const GEO_SIM_US_STATES = [
  "CA",
  "NY",
  "FL",
  "TX",
  "IL",
  "NJ",
  "GA",
  "AZ",
] as const;

export type GeoSimPreset = {
  id: string;
  label: string;
  country: string;
  /** province_ar.geo_code del admin */
  province?: string;
  /** city_ar.geo_code del admin (canónico); se traduce a cf.city al activar */
  cityGeoCode?: string;
  /** cf.city listo para URL (derivado del catálogo si hay cityGeoCode) */
  city?: string;
  state?: string;
  simulateVpn: GeoSimVpnMode;
  intent: string;
};

function arPreset(
  id: string,
  label: string,
  cityGeoCode: string | null,
  provinceFallback: string | null,
  intent: string,
): GeoSimPreset {
  if (cityGeoCode) {
    const e = findArCityByGeoCode(cityGeoCode);
    if (!e) {
      throw new Error(
        `GeoSim preset ${id}: geo_code "${cityGeoCode}" no existe en AR_CITY_CATALOG (admin CTA por región)`,
      );
    }
    return {
      id,
      label,
      country: "AR",
      province: e.provinceCode,
      cityGeoCode: e.geo_code,
      city: simCityParamForEntry(e),
      simulateVpn: "clean",
      intent,
    };
  }
  return {
    id,
    label,
    country: "AR",
    province: provinceFallback || undefined,
    simulateVpn: "clean",
    intent,
  };
}

/**
 * Escenarios solo con geo_code reales del catálogo = filas city_ar / province_ar
 * del admin CTA por región.
 */
export const GEO_SIM_SCENARIO_PRESETS: GeoSimPreset[] = [
  {
    id: "R1-CL",
    label: "R1 · CL DualCTA",
    country: "CL",
    simulateVpn: "clean",
    intent: "Home DualCTA intl · Stripe USD (country, no province_ar)",
  },
  {
    id: "R1-US",
    label: "R1 · US DualCTA",
    country: "US",
    state: "CA",
    simulateVpn: "clean",
    intent: "Home DualCTA intl · Stripe USD",
  },
  arPreset(
    "R2-CABA",
    "R2 · CABA capital",
    "caba",
    null,
    "city_ar caba (CF Buenos Aires) · admin CTA por región",
  ),
  arPreset(
    "R3-Palermo",
    "R3 · city_ar Palermo",
    "palermo",
    null,
    "Barrio CABA · gana sobre province_ar CABA",
  ),
  arPreset(
    "R3-Barracas",
    "R3 · city_ar Barracas",
    "barracas",
    null,
    "Barrio CABA · city_ar barracas",
  ),
  arPreset(
    "R4-Tigre",
    "R4 · GBA Tigre",
    "tigre",
    null,
    "province Buenos Aires · city_ar tigre",
  ),
  arPreset(
    "R4-LaPlata",
    "R4 · BA La Plata",
    "la plata",
    null,
    "Interior BA · city_ar la plata",
  ),
  arPreset(
    "R5-Cordoba",
    "R5 · Córdoba capital",
    "cordoba",
    null,
    "city_ar cordoba · province Córdoba",
  ),
  arPreset(
    "R5-Rosario",
    "R5 · Rosario",
    "rosario",
    null,
    "city_ar rosario · province Santa Fe",
  ),
  {
    id: "R2-CABA-prov-only",
    label: "R2b · solo province_ar CABA",
    country: "AR",
    province: "CABA",
    simulateVpn: "clean",
    intent: "Sin city → solo fila province_ar CABA del admin",
  },
  {
    id: "Q1-Anon",
    label: "Q1 · Privacy VPN",
    country: "US",
    simulateVpn: "anonymous",
    intent: "QA privacy gate (NO visitante real)",
  },
];
